import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LogoDevIcon from "@mui/icons-material/LogoDev";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const PaymentReceipt = ({ payment, companyInfo, paymentSummary = {} }) => {
  const {
    totalAmount = 0,
    paidAmount = 0,
    remainingAmount = 0,
    totalPayments = 0,
    paidPaymentsCount = 0,
    isComplete = false,
  } = paymentSummary;

  const printToPdf = () => {
    const printContent = document.getElementById("printable-receipt");
    if (!printContent) return;

    const originalDisplay = printContent.style.display;
    printContent.style.display = "block";

    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      @page {
        size: A4;
        margin: 15mm;
      }
      @media print {
        body * {
          visibility: hidden;
        }
        #printable-receipt,
        #printable-receipt * {
          visibility: visible;
        }
        #printable-receipt {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
        }
        .no-print {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(styleSheet);
    window.print();
    document.head.removeChild(styleSheet);
    printContent.style.display = originalDisplay;
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount).toLocaleString("fr-FR", {
      style: "currency",
      currency: "MAD",
    });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Box>
      <Button
        variant="contained"
        color="secondary"
        startIcon={<PictureAsPdfIcon />}
        onClick={printToPdf}
        className="no-print"
        sx={{ mb: 2 }}
      >
        Télécharger PDF
      </Button>

      <Box
        id="printable-receipt"
        sx={{
          width: "210mm",
          minHeight: "280mm", // Reduced from 297mm to fit on one page
          mx: "auto",
          bgcolor: "background.paper",
          display: "none",
          p: 3, // Reduced padding
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <LogoDevIcon sx={{ fontSize: 32 }} />
              <Typography variant="h6" fontWeight="bold">
                {companyInfo.name}
              </Typography>
            </Box>
            <Typography variant="body2">{companyInfo.address}</Typography>
            <Typography variant="body2">Tél: {companyInfo.phone}</Typography>
            <Typography variant="body2">Email: {companyInfo.email}</Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="h6" color="primary" gutterBottom>
              REÇU DE PAIEMENT
            </Typography>
            <Typography variant="body2">
              Date: {formatDate(payment.date_paiement || new Date())}
            </Typography>
            <Typography variant="body2">
              N° Reçu: {payment.numero_paiement}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Client and Payment Information */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              INFORMATIONS CLIENT
            </Typography>
            <Table size="small" sx={{ "& td": { py: 0.5 } }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: "none", pl: 0 }}>Client</TableCell>
                  <TableCell sx={{ border: "none" }}>
                    {payment.nom} {payment.prenom}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: "none", pl: 0 }}>N° Vente</TableCell>
                  <TableCell sx={{ border: "none" }}>
                    {payment.numero_vente}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              DÉTAILS DU PAIEMENT
            </Typography>
            <Table size="small" sx={{ "& td": { py: 0.5 } }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: "none", pl: 0 }}>Mode</TableCell>
                  <TableCell sx={{ border: "none" }}>
                    {payment.mode_paiement}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: "none", pl: 0 }}>Date</TableCell>
                  <TableCell sx={{ border: "none" }}>
                    {formatDate(payment.date_paiement)}
                  </TableCell>
                </TableRow>
                {payment.reference_paiement && (
                  <TableRow>
                    <TableCell sx={{ border: "none", pl: 0 }}>
                      Référence
                    </TableCell>
                    <TableCell sx={{ border: "none" }}>
                      {payment.reference_paiement}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Grid>
        </Grid>

        {/* Transaction Details */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            DÉTAILS DE LA TRANSACTION
          </Typography>
          <Table size="small" sx={{ "& td": { py: 0.5 } }}>
            <TableBody>
              <TableRow>
                <TableCell sx={{ border: "none", pl: 0 }}>
                  Montant Total
                </TableCell>
                <TableCell align="right" sx={{ border: "none" }}>
                  {formatCurrency(paymentSummary.totalAmount)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ border: "none", pl: 0 }}>
                  Montant Payé
                </TableCell>
                <TableCell align="right" sx={{ border: "none" }}>
                  {formatCurrency(paymentSummary.paidAmount)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ border: "none", pl: 0 }}>
                  Reste à Payer
                </TableCell>
                <TableCell align="right" sx={{ border: "none" }}>
                  {formatCurrency(paymentSummary.remainingAmount)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ border: "none", pl: 0 }}>Tranche N°</TableCell>
                <TableCell align="right" sx={{ border: "none" }}>
                  {payment.numero_tranche}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>

        {/* Payment Status */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            ÉTAT DES PAIEMENTS
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Tranches Payées:
              </Typography>
              <Typography variant="body1">
                {paymentSummary.paidPaymentsCount} sur{" "}
                {paymentSummary.totalPayments}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Progression:
              </Typography>
              <Typography variant="body1">
                {(
                  (paymentSummary.paidPaymentsCount /
                    paymentSummary.totalPayments) *
                  100
                ).toFixed(1)}
                %
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Payment Complete Message */}
        {paymentSummary.isComplete && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              bgcolor: "success.light",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CheckCircleOutlineIcon color="success" />
            <Box>
              <Typography variant="subtitle1" color="success.dark">
                Paiement Complété
              </Typography>
              <Typography variant="body2" color="success.dark">
                Toutes les tranches ont été payées
              </Typography>
            </Box>
          </Paper>
        )}

        {/* Signatures */}
        <Box sx={{ mt: 4 }}>
          <Grid container spacing={6}>
            <Grid item xs={6}>
              <Divider sx={{ mt: 4 }} />
              <Typography variant="body2" textAlign="center" sx={{ mt: 1 }}>
                Signature Client
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Divider sx={{ mt: 4 }} />
              <Typography variant="body2" textAlign="center" sx={{ mt: 1 }}>
                Signature Autorisée
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="caption" color="text.secondary" display="block">
            Ce reçu est une preuve officielle de paiement
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {companyInfo.name} • {companyInfo.address} • Tél:{" "}
            {companyInfo.phone}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PaymentReceipt;
